/**
 * The full detail for one issue — the console's replacement for the retired
 * `DataQualityIssueDetail` panel.
 *
 * The evidence rendering (`SourceRow`, `hasLocatableProvenance`,
 * `describeBareSource`, `isNumericish`, the `identical_across_years` collapse)
 * is carried over verbatim from it; only the shell around it changes: a heading that
 * takes focus on selection so a screen-reader user is told what loaded, and
 * `fields_involved` chips that are buttons into the Fields view rather than
 * inert text.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Calculator,
  Check,
  ChevronRight,
  Copy,
  FileText,
  Loader2,
  MapPin,
  Sparkles,
  X,
} from 'lucide-react';
import { useValidationIssue } from '../hooks/useDataQuality';
import type { IssueOccurrence, IssueSource } from '../../../types/dataQuality';
import { formatFiscalYear, formatYearRange, humanizeFieldName } from '../utils/dataQualityFormat';
import { SEVERITY_TOKENS, sourceStatusToken, verdictToken } from './dqTokens';

export interface IssueDossierProps {
  companyId: string;
  code: string;
  source: string;
  /** Jump to the Fields view for one canonical field. */
  onSelectField?: (field: string) => void;
  /**
   * Dismiss the dossier — Escape, or the close control.
   *
   * Escape still fires this when `bare`: the accordion row's own header is
   * the toggle affordance, but a reader deep inside the expanded content
   * (arrowed into "Where this number came from") still expects Escape to
   * collapse it without hunting for the header button first.
   */
  onClose?: () => void;
  /**
   * Drop the card shell (border, padding) and the close button, because the
   * container already supplies both. Set when this is nested inside an
   * accordion row — a second close control a few pixels from the row's own
   * collapse toggle read as a glitch.
   */
  bare?: boolean;
  /**
   * Scope `occurrences` to these fiscal years, matching whatever year facet
   * is active in the list above. Omit, or pass an empty array, for every year.
   *
   * One exception the filter has to respect: when the backend has already
   * collapsed identical years into a single representative
   * (`identical_across_years`), that one block stands for every year in
   * `years_affected`, not for its own `fiscal_year` alone. Filtering it by
   * that one year would hide the representative the instant the reader picks
   * any year but the newest, even though the backend's own "same applies to
   * all N periods" line says it covers that year too.
   */
  yearFilter?: number[];
}

/**
 * Make the figures findable inside a raw source excerpt.
 *
 * The snippet is verbatim OCR — it IS the audit trail, so not a character of it
 * is rewritten or reflowed. But an unbroken wall of monospace hides the one
 * number the reader came to check, so numeric runs get weight and a wash. This
 * is presentational only: no `<mark>`, because marking forty numbers in a table
 * dump would make a screen reader announce the emphasis forty times without
 * adding a single fact.
 */
function EmphasiseNumbers({ text }: { text: string }) {
  const parts = text.split(/(-?\d[\d,]*(?:\.\d+)?)/g);

  return (
    <>
      {parts.map((part, index) =>
        /^-?\d[\d,]*(?:\.\d+)?$/.test(part) ? (
          <span key={index} className="font-semibold text-neutral-900">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}

function isNumericish(value: number | string): boolean {
  if (typeof value === 'number') return true;
  return /^[\d\s.,%+\-()]+$/.test(value.trim());
}

function hasLocatableProvenance(source: IssueSource): boolean {
  return !!(
    source.page_number != null ||
    source.snippet ||
    source.document_name ||
    source.xbrl_tag ||
    source.raw_label
  );
}

function describeBareSource(sources: IssueSource[]): string {
  const seen: string[] = [];
  for (const s of sources) {
    const provider = s.provider || s.kind;
    if (!provider) continue;
    const text = s.status ? `${provider} (${s.status})` : provider;
    if (!seen.includes(text)) seen.push(text);
  }
  return seen.join(', ') || 'not recorded';
}

/** Copies the exact snippet text — never a reformatted version of it. */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          // Clipboard access can be denied by the browser; the button simply
          // does nothing rather than throwing in front of the reader.
        }
      }}
      aria-label={copied ? 'Copied' : 'Copy this text'}
      title={copied ? 'Copied' : 'Copy'}
      className="shrink-0 rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}

/** A small pill for one fact about a citation — a page, a tag, a provider. */
function CitePill({ children, mono = false }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border border-neutral-200 bg-white px-1.5 py-0.5 text-[10px] text-neutral-600 ${
        mono ? 'font-mono' : 'font-medium'
      }`}
    >
      {children}
    </span>
  );
}

function SourceRow({
  source,
  index,
  total,
}: {
  source: IssueSource;
  /** Position in the evidence list, for "Source N of M" — only shown when
   *  more than one source backs the finding. */
  index: number;
  total: number;
}) {
  const identifier = source.xbrl_tag || source.raw_label;
  const status = sourceStatusToken(source.status);
  // A computed reconciliation, not a citation of a location — it carries no
  // page and no provider because there is no location to cite. Rendering its
  // generated sentence in the same verbatim `<pre>` block as an OCR excerpt
  // would claim it was lifted off a page, which it was not.
  const isDerivedNarrative = source.status === 'derived';
  const name = source.document_name || identifier || source.provider;

  if (!name && !source.snippet && !source.provider) return null;

  // Falls back to a neutral card when the status is unrecognised — colour is
  // a reinforcement here, never the only thing distinguishing one card from
  // another (the name, the pills and the badge all still render).
  const accent = status?.accent ?? 'border-l-neutral-200';
  const tint = status?.tint ?? 'bg-neutral-50';

  return (
    <div
      className={`overflow-hidden rounded-lg border border-neutral-200 border-l-4 ${accent} shadow-sm`}
    >
      {/* The citation is a header on the excerpt, not another line of body
          text — a filename, a page and a provider are one fact about where
          this came from, and running them into the quote made both harder to
          read. The whole strip is tinted by status, not just the badge in
          its corner, so the axis that says how much to trust this row is
          visible before a single word is read. */}
      <div className={`flex flex-wrap items-start justify-between gap-2 px-3 py-2.5 ${tint}`}>
        <div className="flex min-w-0 items-start gap-2">
          {isDerivedNarrative ? (
            <Calculator
              className={`mt-0.5 h-4 w-4 shrink-0 ${status?.text ?? 'text-neutral-400'}`}
              aria-hidden="true"
            />
          ) : (
            <MapPin
              className={`mt-0.5 h-4 w-4 shrink-0 ${status?.text ?? 'text-neutral-400'}`}
              aria-hidden="true"
            />
          )}
          <div className="min-w-0">
            {total > 1 && (
              <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">
                Source {index + 1} of {total}
              </p>
            )}
            <p className="truncate text-sm font-semibold text-neutral-800">{name}</p>
            {(source.page_number != null || identifier || source.provider) && (
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {source.page_number != null && <CitePill>Page {source.page_number}</CitePill>}
                {identifier && <CitePill mono>{identifier}</CitePill>}
                {source.provider && <CitePill>{source.provider}</CitePill>}
              </div>
            )}
          </div>
        </div>

        {status && (
          <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${status.chip}`}
          >
            <status.icon className="h-3.5 w-3.5" aria-hidden="true" />
            {status.label}
          </span>
        )}
      </div>

      {source.snippet && (
        <div className="relative border-t border-neutral-100">
          {isDerivedNarrative ? (
            // A generated sentence, not an excerpt — it wraps like ordinary
            // prose, in the SAME tint as the header, so the card reads as one
            // cohesive "computed" unit rather than a plain white card with a
            // badge in the corner. Still verbatim: only the whitespace
            // handling differs.
            <p className={`px-3 py-2.5 pr-9 font-mono text-xs leading-6 text-neutral-800 ${tint}`}>
              <EmphasiseNumbers text={source.snippet} />
            </p>
          ) : (
            // `whitespace-pre-wrap`, not prose wrapping: the excerpt's own
            // line breaks are what make a table extract legible as a table.
            // A distinct "code block" grey — not the status tint, which
            // belongs to the header — so an excerpt still reads as a quoted
            // block rather than as ordinary page content. Scrolls inside
            // itself so a long unbroken line cannot widen the dossier.
            <pre className="max-h-48 overflow-auto bg-neutral-50 px-3 py-2.5 pr-9 font-mono text-xs leading-6 text-neutral-800 whitespace-pre-wrap">
              <EmphasiseNumbers text={source.snippet} />
            </pre>
          )}
          {/* Vertically centered rather than pinned to the top — robust for
              a one-line snippet and a ten-line one alike, without needing to
              know which the current data happens to be. */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <CopyButton text={source.snippet} />
          </div>
        </div>
      )}
    </div>
  );
}

function OccurrenceBlock({
  occurrence,
  showPeriodHeading = true,
}: {
  occurrence: IssueOccurrence;
  showPeriodHeading?: boolean;
}) {
  const numbers: Array<[string, number | string]> = [];
  if (occurrence.expected != null) numbers.push(['Expected', occurrence.expected]);
  if (occurrence.observed != null) numbers.push(['Observed', occurrence.observed]);
  if (occurrence.tolerance != null) numbers.push(['Tolerance', occurrence.tolerance]);

  const sources = (occurrence.sources ?? []).filter(Boolean);
  const verdict = verdictToken(occurrence.ai_verdict);

  return (
    <div
      className={
        showPeriodHeading ? 'rounded-lg border border-neutral-200 px-3 py-2.5' : ''
      }
    >
      {showPeriodHeading && (
        <p className="text-xs font-bold uppercase tracking-widest text-neutral-500">
          {occurrence.fiscal_period || formatFiscalYear(occurrence.fiscal_year)}
        </p>
      )}

      {/* ── The answer, first ──────────────────────────────────────────────
          When the backend supplies structured figures they ARE the finding, so
          they lead at full weight and the prose `message` — which restates them
          — recedes to a caption underneath.

          When they are absent the message is all there is, so it takes the
          emphasis instead. The figures are not parsed out of the prose to force
          the strip: deriving numbers by regex from a sentence is exactly the
          guessing this panel refuses. The fix belongs in the payload. */}
      {numbers.length > 0 && (
        <dl className={`flex flex-wrap gap-2 ${showPeriodHeading ? 'mt-2' : ''}`}>
          {numbers.map(([label, value]) => (
            <div
              key={label}
              className="rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5"
            >
              <dt className="text-[10px] uppercase tracking-wide text-neutral-400">{label}</dt>
              <dd
                className={`text-sm text-neutral-900 ${
                  isNumericish(value) ? 'font-mono font-semibold tabular-nums' : ''
                }`}
              >
                {String(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {occurrence.message && (
        <p
          className={
            numbers.length > 0
              ? 'mt-2 max-w-prose text-xs leading-relaxed text-neutral-500'
              : `max-w-prose text-sm leading-relaxed text-neutral-800 ${
                  showPeriodHeading ? 'mt-1.5' : ''
                }`
          }
        >
          {/* This is the backend's own generated sentence, not a verbatim OCR
              excerpt — so, same as a `derived` source's narrative, giving its
              embedded numbers the same bold treatment `SourceRow` already
              gives a snippet is safe: no character changes, only weight. Most
              valuable exactly when `numbers` above is empty (no structured
              expected/observed for this rule), which otherwise leaves a raw,
              comma-less 13-digit figure as the only number on screen. */}
          <EmphasiseNumbers text={occurrence.message} />
        </p>
      )}

      {occurrence.suspect_field && (
        <p className="mt-2 text-xs text-neutral-500">
          Suspect field:{' '}
          <span className="text-neutral-700">{humanizeFieldName(occurrence.suspect_field)}</span>
        </p>
      )}

      {/* ── An opinion, second ─────────────────────────────────────────────
          Keeps its purple attribution — the reader must always be able to tell
          a machine opinion from a deterministic result — but on a left rule
          rather than a filled card, so it no longer competes with the figures
          above it for first read. */}
      {(occurrence.ai_verdict || occurrence.ai_explanation) && (
        <div className="mt-2.5 border-l-2 border-secondary-300 pl-2.5">
          <div className="flex flex-wrap items-center gap-x-1.5">
            <Sparkles className="h-3 w-3 shrink-0 text-secondary-500" aria-hidden="true" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-secondary-600">
              AI review — an opinion, not a fact
            </span>
            {occurrence.ai_verdict && (
              <span className="text-[11px] text-secondary-700">
                · {verdict?.label ?? humanizeFieldName(occurrence.ai_verdict)}
                {occurrence.ai_confidence != null &&
                  ` · ${Math.round(occurrence.ai_confidence * 100)}% confidence`}
              </span>
            )}
          </div>
          {occurrence.ai_explanation && (
            <p className="mt-1 max-w-prose text-xs leading-relaxed text-secondary-900">
              {occurrence.ai_explanation}
            </p>
          )}
        </div>
      )}

      {/* ── The audit trail, last and quietest ─────────────────────────────
          Collapsed by default: it is the most voluminous part of the dossier
          and the least often needed, but when it IS needed nothing substitutes
          for it, so it is one keystroke away rather than behind a fetch. */}
      {sources.length > 0 && (
        <div className="mt-2.5">
          {sources.some(hasLocatableProvenance) ? (
            <details className="group">
              <summary className="flex cursor-pointer list-none items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-400 hover:text-neutral-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500 [&::-webkit-details-marker]:hidden">
                <ChevronRight
                  className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
                  aria-hidden="true"
                />
                <FileText className="h-3 w-3" aria-hidden="true" />
                Where this number came from
                <span className="font-normal normal-case tracking-normal text-neutral-400">
                  ({sources.length} source{sources.length === 1 ? '' : 's'})
                </span>
              </summary>
              <div className="mt-2 space-y-2.5">
                {sources.map((s, i) => (
                  <SourceRow key={i} source={s} index={i} total={sources.length} />
                ))}
              </div>
            </details>
          ) : (
            <p className="text-xs text-neutral-500">Source: {describeBareSource(sources)}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function IssueDossier({
  companyId,
  code,
  source,
  onSelectField,
  onClose,
  bare = false,
  yearFilter,
}: IssueDossierProps) {
  const { data, isLoading, isError } = useValidationIssue(companyId, code, source, true);
  const headingRef = useRef<HTMLHeadingElement>(null);

  /**
   * Announce the dossier once per issue, without stealing focus mid-navigation.
   *
   * The heading takes focus only when the CODE changes and the content has
   * landed — not on every render of the same issue. It used to fire on each
   * selection change while the list still selected on focus, which yanked focus
   * out of the row the reader had just arrowed onto and killed the next
   * keypress. The list now activates explicitly, so by the time this runs the
   * reader has asked for the dossier and moving focus into it is what they want.
   */
  useEffect(() => {
    if (isLoading || !data?.issue) return;
    headingRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-neutral-200 px-4 py-8 text-sm text-neutral-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading detail…
      </div>
    );
  }

  if (isError || !data?.issue) {
    return (
      <div className="rounded-xl border border-neutral-200 px-4 py-8 text-center">
        <p className="text-sm text-neutral-500">Detail for this issue is not available.</p>
      </div>
    );
  }

  const { issue } = data;
  const token = SEVERITY_TOKENS[issue.severity];
  const Icon = token.icon;
  const fields = (issue.fields_involved ?? []).filter(Boolean);
  const occurrences = [...(issue.occurrences ?? [])].sort(
    (a, b) => (b.fiscal_year ?? 0) - (a.fiscal_year ?? 0)
  );

  const collapsed = issue.identical_across_years === true && occurrences.length === 1;
  const periodSpan = formatYearRange(issue.years_affected);
  const periodCount = issue.occurrence_count ?? issue.years_affected?.length ?? 0;

  // Scope to the active year facet — see the prop doc for the collapsed
  // exception. `hasYearFilter` false means "every year", not "no years".
  const hasYearFilter = !!yearFilter && yearFilter.length > 0;
  const visibleOccurrences = !hasYearFilter
    ? occurrences
    : collapsed
      ? (issue.years_affected ?? []).some((year) => yearFilter!.includes(year))
        ? occurrences
        : []
      : occurrences.filter(
          (o) => o.fiscal_year != null && yearFilter!.includes(o.fiscal_year)
        );

  return (
    <div
      className={bare ? 'space-y-3' : 'space-y-3 rounded-xl border border-neutral-200 p-4'}
      // Escape closes, matching every other dismissible pane in the app —
      // still wired when `bare`, so Escape works from anywhere inside the
      // expanded content, not only when focus sits on the row's own header.
      onKeyDown={(event) => {
        if (event.key === 'Escape' && onClose) {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      {bare ? (
        // The accordion row this is nested under already showed the icon,
        // the label, the ambiguous badge, the AI-verdict dot and the year
        // range — repeating the same sentence here read as the panel saying
        // the identical thing twice with no gap between the two. The heading
        // still exists for a screen reader (so opening the row is announced
        // as SOMETHING, not silence) but stays visually invisible; the code
        // remains visible on its own, for a bug report or matching the rule.
        <div className="flex items-baseline justify-between gap-2">
          <h3 ref={headingRef} tabIndex={-1} className="sr-only focus:outline-none">
            Detail for {issue.label}, {issue.code}
          </h3>
          <span className="font-mono text-[11px] text-neutral-400">{issue.code}</span>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <h3
            ref={headingRef}
            tabIndex={-1}
            className="flex flex-1 flex-wrap items-center gap-2 text-sm font-semibold text-neutral-900 focus:outline-none"
          >
            <Icon className={`h-4 w-4 shrink-0 ${token.text}`} aria-hidden="true" />
            {issue.label}
            {/* The label is what a reader reads; the code is here only so it
                can be quoted in a bug report or matched against the ruleset. */}
            <span className="font-mono text-[11px] font-normal text-neutral-400">
              {issue.code}
            </span>
          </h3>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close issue detail"
              className="shrink-0 rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {issue.what_to_do && (
        <p className="max-w-prose text-sm leading-relaxed text-neutral-700">{issue.what_to_do}</p>
      )}

      {fields.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-neutral-500">Fields:</span>
          {fields.map((f) =>
            onSelectField ? (
              <button
                key={f}
                type="button"
                onClick={() => onSelectField(f)}
                className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700 transition-colors hover:bg-primary-50 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500"
              >
                {humanizeFieldName(f)}
              </button>
            ) : (
              <span key={f} className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700">
                {humanizeFieldName(f)}
              </span>
            )
          )}
        </div>
      )}

      {visibleOccurrences.length > 0 ? (
        <div className="space-y-2.5">
          {visibleOccurrences.map((o, i) => (
            <OccurrenceBlock key={`${o.fiscal_year}-${i}`} occurrence={o} showPeriodHeading={!collapsed} />
          ))}
        </div>
      ) : (
        hasYearFilter &&
        occurrences.length > 0 && (
          <p className="rounded-lg border border-dashed border-neutral-200 px-3 py-4 text-center text-xs text-neutral-500">
            This finding has no occurrence in the selected fiscal year.
          </p>
        )
      )}

      {collapsed && periodCount > 1 && visibleOccurrences.length > 0 && (
        <p className="text-xs text-neutral-500">
          The same applies to all {periodCount} periods{periodSpan ? ` (${periodSpan})` : ''}.
        </p>
      )}
    </div>
  );
}
